import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Popover,
  Chip,
  Divider
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatFailureReason } from '../../Utils/failureReasonFormatter';

const ActionStatusWindow = ({ 
  isOpen, 
  onClose, 
  results, 
  anchorPosition = { top: 200, left: 400 }
}) => {
  const { t } = useTranslation();

  // Calculate total processed files
  const totalProcessed = results ? (results.success + results.fail + results.skipped) : 0;
  const failureDetails = results?.failure_details || [];
  const failureReasonByFile = failureDetails.reduce((acc, detail) => {
    if (detail?.file && detail?.reason) {
      acc[detail.file] = detail.reason;
    }
    return acc;
  }, {});
  const nonFileFailures = failureDetails.filter((detail) => !detail?.file);

  return (
    <Popover
      open={isOpen || false}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          border: '1px solid #eaeaef',
          overflow: 'hidden',
          maxWidth: 450,
          minWidth: 400
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        p: 3,
        pb: 2,
        background: 'linear-gradient(145deg, #f8f9ff 0%, #f0f2ff 100%)',
        borderBottom: '1px solid #5757b020',
      }}>
        <AssessmentIcon sx={{ 
          color: '#5757b0', 
          mr: 1.5,
          fontSize: '1.5rem'
        }} />
        <Typography variant="h6" sx={{
          fontWeight: 700,
          color: '#5757b0',
          fontSize: '1.1rem',
          letterSpacing: '-0.2px'
        }}>
          {t('processing_results')}
        </Typography>
      </Box>

      <Box sx={{ 
        p: 3,
        backgroundColor: '#fdfdff'
      }}>
        {results && (
          <>
            {/* Summary Stats */}
            <Typography variant="body2" sx={{ 
              mb: 2, 
              color: '#666',
              fontWeight: 600,
              fontSize: '0.9rem'
            }}>
              Total processed: {totalProcessed} files
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1.5,
              mb: 3,
              flexWrap: 'wrap'
            }}>
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '1rem' }} />}
                label={`${results.success} Success`}
                size="small"
                sx={{
                  backgroundColor: '#f0f0ff',
                  color: '#5757b0',
                  borderColor: '#5757b030',
                  border: '1px solid',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  height: '28px',
                  '& .MuiChip-icon': { 
                    color: '#5757b0',
                    fontSize: '1rem'
                  },
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
              
              {results.fail > 0 && (
                <Chip
                  icon={<ErrorIcon sx={{ fontSize: '1rem' }} />}
                  label={`${results.fail} Failed`}
                  size="small"
                  sx={{
                    backgroundColor: '#fff0f0',
                    color: '#d32f2f',
                    borderColor: '#d32f2f30',
                    border: '1px solid',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    height: '28px',
                    '& .MuiChip-icon': { 
                      color: '#d32f2f',
                      fontSize: '1rem'
                    },
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              )}
              
              {results.skipped > 0 && (
                <Chip
                  icon={<WarningIcon sx={{ fontSize: '1rem' }} />}
                  label={`${results.skipped} Skipped`}
                  size="small"
                  sx={{
                    backgroundColor: '#fff8f0',
                    color: '#f57c00',
                    borderColor: '#f57c0030',
                    border: '1px solid',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    height: '28px',
                    '& .MuiChip-icon': { 
                      color: '#f57c00',
                      fontSize: '1rem'
                    },
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              )}
            </Box>

            {/* Failed Files Section */}
            {results.files_failed && results.files_failed.length > 0 && (
              <>
                <Divider sx={{ 
                  my: 2,
                  borderColor: '#eaeaef'
                }} />
                
                <Typography variant="subtitle2" sx={{ 
                  mb: 2,
                  color: '#d32f2f',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <ErrorIcon sx={{ fontSize: '1rem' }} />
                  {t('failed_files')} ({results.files_failed.length})
                </Typography>
                
                <Box sx={{ 
                  maxHeight: 200,
                  overflow: 'auto',
                  backgroundColor: '#f8f9fc',
                  borderRadius: '8px',
                  border: '1px solid #eaeaef',
                  p: 2
                }}>
                  {results.files_failed.slice(0, 6).map((file, idx) => {
                    const reason = failureReasonByFile[file];
                    const rawReason = reason == null ? '' : String(reason);
                    const displayReason = formatFailureReason(rawReason, t);
                    const reasonTitle = displayReason !== rawReason ? rawReason : '';
                    return (
                      <Box key={idx} sx={{ mb: 1.1, '&:last-child': { mb: 0 } }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.8rem',
                            color: '#666',
                            fontFamily: 'SF Mono, Consolas, Monaco, monospace',
                            wordBreak: 'break-all',
                            lineHeight: 1.3,
                          }}
                        >
                          • {file}
                        </Typography>
                        {reason && (
                          <Typography
                            variant="body2"
                            title={reasonTitle || undefined}
                            sx={{
                              fontSize: '0.78rem',
                              color: '#9a4a4a',
                              mt: 0.3,
                              pl: 1.5,
                              lineHeight: 1.3,
                              wordBreak: 'break-word',
                              cursor: reasonTitle ? 'help' : 'default'
                            }}
                          >
                            {t('error_reason', 'Reason')}: {displayReason}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                  
                  {results.files_failed.length > 6 && (
                    <Typography variant="body2" sx={{ 
                      fontSize: '0.8rem', 
                      fontStyle: 'italic',
                      color: '#999',
                      mt: 1,
                      textAlign: 'center'
                    }}>
                      ... and {results.files_failed.length - 6} more files
                    </Typography>
                  )}
                </Box>
              </>
            )}

            {nonFileFailures.length > 0 && (
              <>
                <Divider sx={{ 
                  my: 2,
                  borderColor: '#eaeaef'
                }} />
                <Typography variant="subtitle2" sx={{ 
                  mb: 2,
                  color: '#d32f2f',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <ErrorIcon sx={{ fontSize: '1rem' }} />
                  {t('failed_details', 'Failure Details')}
                </Typography>
                <Box sx={{
                  maxHeight: 160,
                  overflow: 'auto',
                  backgroundColor: '#f8f9fc',
                  borderRadius: '8px',
                  border: '1px solid #eaeaef',
                  p: 2
                }}>
                  {nonFileFailures.slice(0, 6).map((detail, idx) => {
                    const label = detail?.item_id
                      ? `${t('failed_item', 'Item')} ${detail.item_id}`
                      : detail?.action_id
                        ? `${t('failed_action', 'Action')} ${detail.action_id}`
                        : t('failed_entry', 'Failure');
                    return (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{
                          fontSize: '0.8rem',
                          color: '#666',
                          mb: 0.6,
                          wordBreak: 'break-word',
                          lineHeight: 1.3,
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        • {label}: {String(detail?.reason || t('failed_unknown', 'Unknown error'))}
                      </Typography>
                    );
                  })}
                </Box>
              </>
            )}
          </>
        )}

        {/* Footer Button */}
        <Box sx={{ 
          mt: 3,
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Button 
            onClick={onClose}
            variant="contained"
            sx={{
              background: 'linear-gradient(145deg, #5757b0 0%, #6d6dc8 100%)',
              color: 'white',
              borderRadius: '10px',
              fontWeight: 600,
              px: 4,
              py: 1.2,
              minWidth: '120px',
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 4px 12px rgba(87, 87, 176, 0.2)',
              '&:hover': {
                background: 'linear-gradient(145deg, #4a4a90 0%, #5d5db0 100%)',
                boxShadow: '0 6px 16px rgba(87, 87, 176, 0.25)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {t('close')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default ActionStatusWindow;
